import { Injectable } from '@angular/core';
import { MilkControl } from './milk-control';
import { MilkControlSummary } from './milk-control-Summary';
import { catchError, map, switchMap } from 'rxjs/operators';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { BaseIndexedDbRepository } from 'libs/arch/src/lib/repositories/indexed-db/indexdb-repository';
import { SynchronizationService } from 'libs/arch/src/lib/repositories/indexed-db/synchronization.service';
import { ApiService } from '@homeleite/arch';
import { from, Observable, of } from 'rxjs';
import { MilkedAnimalRepository } from '../animals/events/milked-animal/milked-animal-repository';

@Injectable({ providedIn: 'root' })
export class MilkControlRepository extends BaseIndexedDbRepository<
    MilkControlSummary,
    MilkControl
> {
    protected readonly storeName: string = 'milkControls';

    protected getResourcePath(): string {
        return 'milkControls';
    }

    protected getSearchableFields(): string[] {
        return ['status', 'producer.name', 'property.name'];
    }

    protected override mapToEntity(dbItem: MilkControl): MilkControl {
        return MilkControl.fromJson(dbItem);
    }

    protected mapToSummary(item: MilkControl): MilkControlSummary {
        return new MilkControlSummary(item.id);
    }

    constructor(
        protected override readonly dbService: NgxIndexedDBService,
        protected override readonly synchronizationService: SynchronizationService,
        private milkedAnimalRepository: MilkedAnimalRepository,
        private apiService: ApiService
    ) {
        super(dbService, synchronizationService);
    }

    findLastFinishedBy(producerId: string): Observable<MilkControl | null> {
        return this.dbService.getAll<MilkControl>(this.storeName).pipe(
            map((controlsAsJson) => controlsAsJson.map((dbItem) => this.mapToEntity(dbItem))),

            map((milkControls) => {
                const filtered = milkControls.filter(
                    (it) =>
                        (it.producer?.id === producerId ||
                            (it as any).producerId === producerId) &&
                        it.status === 'CLOSED'
                );

                return (
                    filtered.sort(
                        (a, b) => (b.finishedAt?.getTime() || 0) - (a.finishedAt?.getTime() || 0)
                    )[0] || null
                );
            }),
            catchError((err) => {
                console.error('Error fetching last control:', err);
                return of(null);
            })
        );
    }

    findUnfinishedBy(producerId: string): Observable<MilkControl | null> {
        return this.dbService.getAll<MilkControl>(this.storeName).pipe(
            map((controlsAsJson) => controlsAsJson.map((dbItem) => this.mapToEntity(dbItem))),

            map((milkControls) => {
                const filtered = milkControls.filter(
                    (it) =>
                        it.producer?.id === producerId &&
                        it.status === 'OPEN'
                );
                return (
                    filtered.sort(
                        (a, b) => (b.startedAt?.getTime() || 0) - (a.startedAt?.getTime() || 0)
                    )[0] || null
                );
            }),
            switchMap((control) => {
                if (!control) return of(null);

                return from(this.milkedAnimalRepository.listByControlId(control.id)).pipe(
                    map((freshAnimals) => {
                        if (control.milkings && control.milkings.length > 0) {
                            control.milkings.forEach(milking => {
                                milking.milkedAnimals = freshAnimals.filter(
                                    milkedAnimal => milkedAnimal.milkingId === milking.id
                                );
                            });
                        }

                        return control;
                    })
                );
            }),

            catchError((err) => {
                console.error('Error fetching current control:', err);
                return of(null);
            })
        );
    }

    findPenultimateFinishedBy(producerId: string): Observable<MilkControl | null> {
        return this.dbService.getAll<MilkControl>(this.storeName).pipe(
            map((controlsAsJson) => controlsAsJson.map((dbItem) => this.mapToEntity(dbItem))),

            map((milkControls) => {
                const filtered = milkControls.filter(
                    (it) =>
                        it.producer?.id === producerId &&
                        it.status === 'CLOSED'
                );

                const sorted = filtered.sort(
                    (a, b) => (b.finishedAt?.getTime() || 0) - (a.finishedAt?.getTime() || 0)
                );

                return sorted[1] || null;
            }),
            catchError((err) => {
                console.error('Error fetching penultimate control:', err);
                return of(null);
            })
        );
    }

    findForMilkingContext(controlId: string, milkingId: string): Observable<MilkControl | null> {
        return this.dbService.getByKey<MilkControl>(this.storeName, controlId).pipe(
            switchMap((controlJson) => {
                if (!controlJson) return of(null);

                const control = this.mapToEntity(controlJson);

                return from(this.milkedAnimalRepository.listByMilkingId(milkingId)).pipe(
                    map((freshAnimals) => {

                        const targetMilking = control.milkings?.find(milking => milking.id === milkingId);

                        if (targetMilking) {
                            targetMilking.milkedAnimals = freshAnimals;
                        }

                        return control;
                    })
                );
            })
        );
    }

    find(id: string): Observable<MilkControl | null> {
        return this.dbService.getByKey<MilkControl>(this.storeName, id).pipe(
            switchMap((controlJson) => {
                if (!controlJson) {
                    return this.fetchFullFromApi(id);
                }

                const control = this.mapToEntity(controlJson);

                return this.milkedAnimalRepository.listByControlId(id).pipe(
                    map((freshAnimals) => {

                        if (control.milkings && control.milkings.length > 0) {
                            control.milkings.forEach((milking) => {
                                milking.milkedAnimals = freshAnimals.filter(
                                    (animal) => animal.milkingId === milking.id
                                );
                            });
                        }

                        return control;
                    }),
                    switchMap((local) => {
                        const hasDetail = local?.milkings?.some(
                            (m) => (m.milkedAnimals?.length ?? 0) > 0
                        );
                        return hasDetail ? of(local) : this.fetchFullFromApi(id).pipe(
                            map((remote) => remote ?? local)
                        );
                    })
                );
            })
        );
    }

    /** lastFinishedBy agora é summary — histórico precisa do DTO completo sob demanda. */
    private fetchFullFromApi(id: string): Observable<MilkControl | null> {
        return this.apiService.root
            .path(this.getResourcePath(), id)
            .getJson<any>()
            .pipe(
                map((json) => (json ? this.mapToEntity(json) : null)),
                catchError((err) => {
                    console.error('Error fetching milk control from API:', err);
                    return of(null);
                })
            );
    }
}
